import {
  Card,
  CardContent,
  Collapse,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Button,
  IconButton,
} from "@mui/material";
import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { KPI_DASHBOARD } from "../../constants";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  LabelList,
} from "recharts";
import { useNavigate } from "react-router-dom";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import DownloadIcon from "@mui/icons-material/Download";
import { Award } from "lucide-react";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

const KPIDashboardComponent = ({ filters }) => {
  const [reportData, setReportData] = useState([]);

  const navigate = useNavigate();

  const fetchTableData = async () => {
    const { startDate, endDate } = filters;

    const formattedStartDate =
      startDate == null || startDate === ""
        ? dayjs().subtract(6, "days").format("YYYY-MM-DD")
        : dayjs(startDate).format("YYYY-MM-DD");

    const formattedEndDate =
      endDate == null || endDate === ""
        ? dayjs().format("YYYY-MM-DD")
        : dayjs(endDate).format("YYYY-MM-DD");

    try {
      const response = await axios.get(
        `${KPI_DASHBOARD}?startDate=${formattedStartDate}&endDate=${formattedEndDate}`
      );
      setReportData(response.data.data);
    } catch (error) {
      console.error("Error fetching data", error);
    }
  };

  useEffect(() => {
    fetchTableData();
  }, [filters]);

  const today = dayjs().format("YYYY-MM-DD");

  const startOfMonth = dayjs().startOf("month").format("YYYY-MM-DD");

  const today1 = dayjs(); // use 'today' consistently
  const currentYear = today1.year();

  // If today's date is before April 1st, financial year started last year
  const financialYearStart = today1.isBefore(dayjs(`${currentYear}-04-01`))
    ? dayjs(`${currentYear - 1}-04-01`)
    : dayjs(`${currentYear}-04-01`);

  const startOfYear = financialYearStart.format("YYYY-MM-DD");

  const getStartOfQuarter = () => {
    const month = dayjs().month(); // 0-indexed: Jan = 0, Apr = 3, etc.
    const quarterStartMonth = Math.floor(month / 3) * 3; // e.g., Apr-Jun -> 3
    return dayjs()
      .month(quarterStartMonth)
      .startOf("month")
      .format("YYYY-MM-DD");
  };

  const startOfQuarter = getStartOfQuarter();
  const getQuarterLabel = (date = dayjs()) => {
    const month = date.month(); // 0-indexed
    if (month >= 3 && month <= 5) return "Q1"; // Apr–Jun
    if (month >= 6 && month <= 8) return "Q2"; // Jul–Sep
    if (month >= 9 && month <= 11) return "Q3"; // Oct–Dec
    return "Q4"; // Jan–Mar
  };

  const fetchKPIData = async (startDate, endDate) => {
    const response = await axios.get(
      `${KPI_DASHBOARD}?startDate=${startDate}&endDate=${endDate}`
    );
    return response.data.data;
  };

  const [mtdData, setMtdData] = useState(null);
  const [ytdData, setYtdData] = useState(null);
  const [quarterData, setQuarterData] = useState(null);

  useEffect(() => {
    const loadAll = async () => {
      try {
        const [mtd, ytd, quarter] = await Promise.all([
          fetchKPIData(startOfMonth, today),
          fetchKPIData(startOfYear, today),
          fetchKPIData(startOfQuarter, today),
        ]);
        setMtdData(mtd);
        setYtdData(ytd);
        setQuarterData(quarter);
      } catch (err) {
        console.error("Error fetching KPI data", err);
      }
    };
    loadAll();
  }, []);

  const [selectedBDM, setSelectedBDM] = useState(null);

  const summaryData = reportData?.summary || {};
  const targetData = reportData?.team_targets || {};

  const handleCardClick = (bdmId) => {
    setSelectedBDM((prev) => (prev === bdmId ? null : bdmId));
    navigate("/kpiDetails", {
      state: {
        bdmId: bdmId,
        startDate: filters.startDate,
        endDate: filters.endDate,
      },
    });
  };

  const downloadBDMExcel = (apiData) => {
    const { bdms, date_range, summary } = apiData || {};
    const { start_date, end_date } = date_range || {};

    // 1. Excel Data Header
    const sheetData = [
      [
        `Parivartan Sales Team Meeting, Field Visit & Travel Summary Report ${start_date} to ${end_date}`,
      ],
      [
        "Sr",
        "Parivartan Sales Team",
        "Employee Id",
        "Role",
        "Unique Customers (in a month)",
        "Group Meeting Count (5+Customers)",
        "No.of Customer Attended in Group Meetings (5+Customers/Meeting)",
        "Group Meeting Count (20 Customers)",
        "No.of Customer Attended in Group Meetings (20+Customers/Meeting)",
        "Individual Meeting",
        "Field (Site/Farmer) Visit Customers",
        "Office Visit (HO/RO/BO)",
        "Visited Branch Count",
      ],
    ];

    // 2. Add BDM Rows
    bdms?.forEach((item, index) => {
      const stats = item.statistics || {};
      const groupStats = stats.group_meetings || {};
      const largeGroupStats = stats.large_group_meetings || {};
      const visits =
        (stats.bo_visit || 0) + (stats.ho_visit || 0) + (stats.ro_visit || 0);

      sheetData.push([
        index + 1,
        item.bdm?.name || "",
        item.bdm?.id || "",
        "Parivartan BDM", // static role
        stats.total_unique_meetings || 0,
        groupStats.unique_meetings || 0,
        groupStats.total_customers || 0,
        largeGroupStats?.unique_meetings || 0,
        largeGroupStats?.total_customers || 0,
        stats.total_meetings || 0,
        stats.total_unique_field_visits || 0,
        `${stats.ho_visit || 0} HO / ${stats.ro_visit || 0} RO / ${
          stats.bo_visit || 0
        } BO`,
        visits,
      ]);
    });

    // 3. Add Summary Row
    const totalVisits =
      (summary?.bo_visit || 0) +
      (summary?.ho_visit || 0) +
      (summary?.ro_visit || 0);
    const totalRow = [
      "",
      "Total",
      "",
      "",
      summary?.total_unique_meetings || 0,
      summary?.group_meetings?.unique_meetings || 0,
      summary?.group_meetings?.total_customers || 0,
      summary?.large_group_meetings?.unique_meetings || 0,
      summary?.large_group_meetings?.total_customers || 0,
      summary?.total_meetings || 0,
      summary?.total_unique_field_visits || 0,
      `${summary?.ho_visit || 0} HO / ${summary?.ro_visit || 0} RO / ${
        summary?.bo_visit || 0
      } BO`,
      totalVisits,
    ];
    sheetData.push(totalRow);

    // 4. Create Excel Sheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // 5. Merge the Title Row (first row across all columns)
    ws["!merges"] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 15 } }];

    // 6. Optional Column Widths
    ws["!cols"] = Array(16).fill({ width: 25 });

    XLSX.utils.book_append_sheet(wb, ws, "BDM Summary");

    // 7. Download Excel
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([wbout], { type: "application/octet-stream" }),
      "BDM_Summary_Report.xlsx"
    );
  };

  const chartData = [
    {
      name: "Group Meetings",
      Action: summaryData.total_group_meetings || 0,
      Target: targetData?.group_meetings?.target || 0,
    },
    {
      name: "Unique Customers",
      Action: summaryData.total_unique_meetings || 0,
      Target: 0,
    },
    {
      name: "Individual Meetings",
      Action: summaryData.total_meetings || 0,
      Target: targetData?.unique_meetings?.target || 0,
    },
    {
      name: "Field Visits",
      Action: summaryData.total_unique_field_visits || 0,
      Target: targetData?.unique_field_visits?.target || 0,
    },
  ];

  const getChartData = (summaryData, targetData) => [
    {
      name: "Group Meetings",
      Action: summaryData?.total_group_meetings || 0,
      Target: targetData?.group_meetings.target || 0,
    },
    {
      name: "Unique Customers",
      Action: summaryData?.total_unique_meetings || 0,
      Target: 0,
    },
    {
      name: "Individual Meetings",
      Action: summaryData?.total_meetings || 0,
      Target: targetData?.unique_meetings.target || 0,
    },
    {
      name: "Field Visits",
      Action: summaryData?.total_unique_field_visits || 0,
      Target: targetData?.unique_field_visits.target || 0,
    },
  ];

  return (
    <>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
        >
          {/* Date Range Summary */}
          {reportData?.date_range && (
            <Typography varianactiont="h6" sx={{ mb: 2, fontWeight: "bold" }}>
              Date Range:{" "}
              {dayjs(reportData.date_range.start_date).format("DD MMM YYYY")} -{" "}
              {dayjs(reportData.date_range.end_date).format("DD MMM YYYY")}
            </Typography>
          )}

          <IconButton
            color="primary"
            onClick={() => downloadBDMExcel(reportData)}
            style={{
              border: "1px solid #fff", // Change border color to white for contrast
              borderRadius: "4px",
              margin: "5px",
              width: "40px", // Increased width for a more comfortable click area
              height: "40px", // Increased height for a more comfortable click area
              justifyContent: "center",
              alignItems: "center",
              backgroundColor: "#097969",
              color: "#fff",
              transition: "background-color 0.3s, transform 0.2s", // Smooth transition effects
              boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)", // Add shadow for depth
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#0B9B73"; // Darker shade on hover
              e.currentTarget.style.transform = "scale(1.05)"; // Slightly scale up on hover
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#097969"; // Original color
              e.currentTarget.style.transform = "scale(1)"; // Reset scale
            }}
          >
            <DownloadIcon></DownloadIcon>
          </IconButton>
        </Box>

        <Box sx={{ display: "flex", gap: 4, mb: 2 }}>
          <Typography variant="body1">
            <strong>Total BDMs:</strong> {reportData?.bdm_count}
          </Typography>
          <Typography variant="body1">
            <strong>Active BDMs:</strong> {reportData?.bdms?.length ?? 0}
          </Typography>
        </Box>

        {/* Summary Chart */}
        <Grid container spacing={3} sx={{ mb: 2 }}>
          {/* Summary Overview */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                📊 Summary Overview (
                {dayjs(reportData?.date_range?.start_date).format("DD/MM/YYYY")}{" "}
                to{" "}
                {dayjs(reportData?.date_range?.end_date).format("DD/MM/YYYY")})
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={chartData}>
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Action" stackId="a" fill="#42a5f5">
                    <LabelList
                      dataKey="Action"
                      position="insideTop"
                      fill="#fff"
                    />
                  </Bar>
                  <Bar
                    dataKey="Target"
                    stackId="a"
                    fill="#ffa726"
                    radius={[8, 8, 0, 0]}
                  >
                    <LabelList
                      dataKey="Target"
                      position="insideTop"
                      fill="#fff"
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* MTD Overview */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                📅 MTD Overview ({dayjs(startOfMonth).format("DD/MM/YYYY")} to{" "}
                {dayjs(today).format("DD/MM/YYYY")})
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={getChartData(mtdData?.summary, mtdData?.team_targets)}
                >
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Action" stackId="b" fill="#ef5350">
                    <LabelList
                      dataKey="Action"
                      position="insideTop"
                      fill="#fff"
                    />
                  </Bar>
                  <Bar
                    dataKey="Target"
                    stackId="b"
                    fill="#66bb6a"
                    radius={[8, 8, 0, 0]}
                  >
                    <LabelList
                      dataKey="Target"
                      position="insideTop"
                      fill="#fff"
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* YTD Overview */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                📆 YTD Overview ({dayjs(startOfYear).format("DD/MM/YYYY")} to{" "}
                {dayjs(today).format("DD/MM/YYYY")})
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={getChartData(ytdData?.summary, ytdData?.team_targets)}
                >
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Action" stackId="b" fill="#26a69a">
                    <LabelList
                      dataKey="Action"
                      position="insideTop"
                      fill="#fff"
                    />
                  </Bar>
                  <Bar
                    dataKey="Target"
                    stackId="b"
                    fill="#fdd835"
                    radius={[8, 8, 0, 0]}
                  >
                    <LabelList
                      dataKey="Target"
                      position="insideTop"
                      fill="#000"
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Quarter Overview */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                🗓️ Quarter Overview ({getQuarterLabel(dayjs())}) – (
                {dayjs(startOfQuarter).format("DD/MM/YYYY")} to{" "}
                {dayjs(today).format("DD/MM/YYYY")})
              </Typography>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={getChartData(
                    quarterData?.summary,
                    quarterData?.team_targets
                  )}
                >
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Action" stackId="b" fill="#8e24aa">
                    <LabelList
                      dataKey="Action"
                      position="insideTop"
                      fill="#fff"
                    />
                  </Bar>
                  <Bar
                    dataKey="Target"
                    stackId="b"
                    fill="#29b6f6"
                    radius={[8, 8, 0, 0]}
                  >
                    <LabelList
                      dataKey="Target"
                      position="insideTop"
                      fill="#fff"
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          {reportData?.bdms
            ?.slice()
            .sort((a, b) => {
              const aValue =
                a.performance_summary?.overall_target_achievement ?? 0;
              const bValue =
                b.performance_summary?.overall_target_achievement ?? 0;
              return bValue - aValue;
            })
            .map((bdmObj, index) => {
              const rank = index + 1;
              const {
                statistics = {},
                targets = {},
                performance_summary = {},
              } = bdmObj;
              const officeVisit =
                (statistics.ho_visit || 0) +
                (statistics.ro_visit || 0) +
                (statistics.bo_visit || 0);

              const groupTarget = targets.group_meetings?.target ?? 0;
              const groupAchieved = targets.group_meetings?.achievement ?? 0;
              const groupProgress = targets.group_meetings?.percentage ?? 0;

              const uniqueTarget = targets.unique_meetings?.target ?? 0;
              const uniqueAchieved = targets.unique_meetings?.achievement ?? 0;
              const uniqueProgress = targets.unique_meetings?.percentage ?? 0;

              const fieldTarget = targets.unique_field_visits?.target ?? 0;
              const fieldAchieved =
                targets.unique_field_visits?.achievement ?? 0;
              const fieldProgress =
                targets.unique_field_visits?.percentage ?? 0;

              const officeTarget = 10; // or fetch dynamically
              const officeAchieved = officeVisit;
              const officeProgress = (officeAchieved / officeTarget) * 100; // allow >100%

              const getColor = (value) => {
                if (value >= 100) return "bg-green-600";
                if (value >= 75) return "bg-blue-500";
                if (value >= 50) return "bg-yellow-500";
                return "bg-red-500";
              };

              return (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      transition: "0.3s",
                      position: "relative", // 👈 Required for absolute positioning
                      "&:hover": { boxShadow: 6 },
                      border:
                        selectedBDM === bdmObj.bdm.id
                          ? "2px solid #1976d2"
                          : "1px solid #e0e0e0",
                    }}
                    onClick={() => handleCardClick(bdmObj.bdm.id)}
                  >
                    <CardContent>
                      {/* 👑 Rank & Achievement Badge */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          backgroundColor: "#f5f5f5",
                          color: "#333",
                          borderRadius: "8px",
                          px: 1.5,
                          py: 0.5,
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.1)",
                        }}
                      >
                        {rank <= 3 && (
                          <EmojiEventsIcon
                            sx={{ fontSize: 18, color: "#FFD700" }}
                          />
                        )}
                        #{rank} •{" "}
                        {performance_summary.overall_target_achievement ?? 0}%
                      </Box>
                      <Typography variant="h6" gutterBottom>
                        {bdmObj.bdm.name} ({bdmObj.bdm.id})
                      </Typography>

                      {/* Group Meetings */}
                      <div className="mb-3">
                        <div className="flex justify-between text-sm text-gray-600 mb-1 items-center">
                          <div className="flex items-center gap-1">
                            Group Meetings ({groupAchieved}/{groupTarget})
                            {groupProgress > 100 && (
                              <WorkspacePremiumIcon
                                sx={{ fontSize: 20, color: "#f59e0b" }}
                              />
                            )}
                          </div>
                          <div>{groupProgress}%</div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                          <div
                            style={{
                              width: `${Math.min(groupProgress, 100)}%`,
                              background:
                                groupProgress > 100
                                  ? "linear-gradient(to right, #4ade80, #22c55e)"
                                  : undefined,
                            }}
                            className={`h-2.5 rounded-full transition-all duration-300 ease-in-out ${
                              groupProgress <= 100
                                ? getColor(groupProgress)
                                : ""
                            }`}
                          ></div>
                        </div>
                      </div>

                      {/* Unique Customers */}
                      <div className="mb-3">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <div>
                            Unique Customers ({uniqueAchieved}/{uniqueTarget})
                            {uniqueProgress > 100 && (
                              <WorkspacePremiumIcon
                                sx={{ fontSize: 20, color: "#f59e0b" }}
                              />
                            )}
                          </div>
                          <div>{uniqueProgress}%</div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            style={{
                              width: `${Math.min(uniqueProgress, 100)}%`,
                              background:
                                uniqueProgress > 100
                                  ? "linear-gradient(to right, #4ade80, #22c55e)"
                                  : undefined,
                            }}
                            className={`h-2.5 rounded-full transition-all duration-300 ease-in-out ${
                              uniqueProgress <= 100
                                ? getColor(uniqueProgress)
                                : ""
                            }`}
                          ></div>
                        </div>
                      </div>

                      {/* Field Visits */}
                      <div className="mb-3">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <div>
                            Field Visits ({fieldAchieved}/{fieldTarget})
                            {fieldProgress > 100 && (
                              <WorkspacePremiumIcon
                                sx={{ fontSize: 20, color: "#f59e0b" }}
                              />
                            )}
                          </div>
                          <div>{fieldProgress}%</div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            style={{
                              width: `${Math.min(fieldProgress, 100)}%`,
                              background:
                                fieldProgress > 100
                                  ? "linear-gradient(to right, #4ade80, #22c55e)"
                                  : undefined,
                            }}
                            className={`h-2.5 rounded-full transition-all duration-300 ease-in-out ${
                              fieldProgress <= 100
                                ? getColor(fieldProgress)
                                : ""
                            }`}
                          ></div>
                        </div>
                      </div>

                      {/* Office Visits */}
                      <div className="mb-3">
                        <div className="flex justify-between text-sm text-gray-600 mb-1">
                          <div>
                            Office Visits ({officeAchieved}/{officeTarget})
                            {officeProgress > 100 && (
                              <WorkspacePremiumIcon
                                sx={{ fontSize: 20, color: "#f59e0b" }}
                              />
                            )}
                          </div>
                          <div>{Math.round(officeProgress)}%</div>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div
                            style={{
                              width: `${Math.min(officeProgress, 100)}%`,
                              background:
                                officeProgress > 100
                                  ? "linear-gradient(to right, #4ade80, #22c55e)"
                                  : undefined,
                            }}
                            className={`h-2.5 rounded-full transition-all duration-300 ease-in-out ${
                              officeProgress <= 100
                                ? getColor(officeProgress)
                                : ""
                            }`}
                          ></div>
                        </div>
                      </div>

                      {/* Overall Achievement */}
                      <Typography
                        variant="body2"
                        className="text-right text-sm font-semibold text-gray-700"
                      >
                        Total Achievement:{" "}
                        {performance_summary.overall_target_achievement ?? 0}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
        </Grid>
      </Box>
    </>
  );
};

export default KPIDashboardComponent;
